import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { EventVenueType } from '@core/enums/event/event-venue-type.enum';
import { CartItemViewModel } from '@core/models/cart/cart-item-view-model.model';
import { Cart } from '@core/models/cart/cart.model';
import { SelectEventRequest } from '@core/models/cart/select-event-request.model';
import { EventView } from '@core/models/event/event-view.model';
import { PriceCategory } from '@core/models/product/price-category.model';
import { CartStoreService } from '@core/services/cart/cart-store.service';
import { CartService } from '@core/services/cart/cart.service';
import { EventsService } from '@core/services/event/events.service';
import { AAGUID, ApiErrorResponse, AppError, DateTimeFormat } from '@interticket/core';
import { Callout } from '@shared/models/callout/callout.model';
import { CalloutType, ErrorService } from 'common';
import { combineLatest, Observable, Subject } from 'rxjs';
import { finalize, map, take, takeUntil } from 'rxjs/operators';
import { MAXIMUM_CART_CAPACITY } from '../../config/add-to-cart.config';

@Component({
  selector: 'app-add-to-cart',
  templateUrl: './add-to-cart.component.html',
  styleUrls: ['./add-to-cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToCartComponent implements OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private _eventId: AAGUID;

  readonly onlineEventCallout: Callout = new Callout({
    type: CalloutType.WARNING,
    description: 'stemx_online_ticket_info',
  });

  readonly customersCountryIsDisabledCallout: Callout = new Callout({
    type: CalloutType.WARNING,
    title: 'event_not_available_in_customer_country_title',
    description: 'event_not_available_in_customer_country_description',
  });

  readonly freeEventCallout: Callout = new Callout({
    type: CalloutType.SUCCESS,
    title: 'free_event',
    description: 'label_free_event_callout_description',
  });

  readonly selectionEventNotFoundCallout: Callout = new Callout({
    type: CalloutType.WARNING,
    title: 'audit_error_popup_title',
    description: 'event_not_found_selection',
  });

  readonly showOnlineWarningMessage$ = this.cartStoreService.cartItemsGrouped$
    .pipe(
      map((cartItemsGrouped: CartItemViewModel[][][]) => cartItemsGrouped?.some((cartItems: CartItemViewModel[][]) => {
        if (cartItems[0][0]?.eventId !== this._eventId || !this.eventView.onlineEventType) {
          return false;
        }
        if (cartItems?.length > 1) {
          return true;
        }
        return cartItems.some((value: CartItemViewModel[]) => value.length > 0);
      }))
    );

  readonly disableNextButton$ = this.cartStoreService.cartItemsGrouped$
    .pipe(
      map((cartItemsGrouped: CartItemViewModel[][][]) =>
        cartItemsGrouped?.some((cartItems: CartItemViewModel[][]) => cartItems[0][0]?.eventId === this._eventId))
    );

  readonly dateTimeFormat = DateTimeFormat;

  errorCallout: Callout;
  eventView: EventView;
  isDisabled: AAGUID;
  requestError: AAGUID;
  requestErrorMessage: string;
  numberOfCartItems: number;
  isLoading = true;

  @Input()
  set eventId(id: AAGUID) {
    if (id && (id !== this._eventId || this.errorCallout)) {
      this.isLoading = true;
      this._eventId = id;
      this.setTicketState();
    }
  }

  get eventId(): AAGUID {
    return this._eventId;
  }

  constructor(
    private cartService: CartService,
    private cartStoreService: CartStoreService,
    private eventService: EventsService,
    private cdr: ChangeDetectorRef,
    private errorService: ErrorService
  ) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setTicketState(): void {
    combineLatest([
      this.cartStoreService.cart$,
      this.eventService.getEvent(this.eventId),
    ])
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = this.isDisabled = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: ([cart, eventView]) => {
          this.eventView = eventView;
          this.errorCallout = null;
          this.numberOfCartItems = cart?.totalAmount || 0;
        },
        error: (error: AppError) => {
          this.eventView = null;
          if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound) {
            this.errorCallout = this.selectionEventNotFoundCallout;
          } else {
            this.errorCallout = this.errorService.getErrorCallout(error);
          }
        },
      });
  }

  updateCart(selectedAmount: string, priceCategory: PriceCategory): void {
    const difference: number = +selectedAmount - priceCategory.amount;
    let updateCart: Observable<Cart>;

    if (difference < 0) {
      updateCart = this.cartService.deleteFromCart(priceCategory.id, difference * -1);
    } else {
      const selectEventRequest = new SelectEventRequest({
        eventId: this.eventView.id,
        priceCategoryId: priceCategory.id,
        quantity: difference,
        knownPrice: priceCategory.price,
      });

      updateCart = this.cartService.addToCart(selectEventRequest);
    }

    this.isDisabled = priceCategory.id;
    updateCart
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => this.setTicketState())
      )
      .subscribe({
        next: () => {
          this.requestError = null;
          this.requestErrorMessage = null;
        },
        error: (error: AppError) => {
          this.requestError = priceCategory.id;
          this.requestErrorMessage = error instanceof ApiErrorResponse ? error.getErrorLabels()[0] : 'unfortunately_not_complete_request';
        },
      });
  }

  getDropdownOptions(availableCapacity: number, currentAmount: number) {
    let maxAmount = 1;
    if (this.eventView?.type !== EventVenueType.ONLINE) {
      const cartCapacity = MAXIMUM_CART_CAPACITY - (this.numberOfCartItems - currentAmount);
      const priceCategoryCapacity = availableCapacity === null ? cartCapacity : Math.min(availableCapacity + currentAmount, cartCapacity);
      const eventCapacity = this.eventView.availableCapacity === null ? cartCapacity : this.eventView.availableCapacity + currentAmount;
      const maxAvailableCapacity = Math.min(priceCategoryCapacity, eventCapacity);
      maxAmount = Math.max(maxAvailableCapacity, currentAmount);
    }

    const dropdownOptions: number[] = [];
    for (let i = 0; i <= maxAmount; i++) {
      dropdownOptions.push(i);
    }

    return dropdownOptions;
  }

  trackByFn(index: number): number {
    return index;
  }

}
